import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import "@/components/storefront-hunter/storefrontHunter.css";
import {
  StorefrontPageHeader,
  StorefrontSearchPanel,
  StoreSummary,
  StorefrontResultsHeader,
} from "@/components/storefront-hunter/StorefrontSections";
import { StorefrontProductCard } from "@/components/storefront-hunter/StorefrontProductCard";
import { StorefrontProductSkeleton } from "@/components/storefront-hunter/StorefrontSections";
import { StorefrontFilters } from "@/components/storefront-hunter/StorefrontFilters";
import { StorefrontPagination } from "@/components/storefront-hunter/StorefrontPagination";
import { SellerWatch, useSellerWatchDock } from "@/components/storefront-hunter/SellerWatch";
import { RecentlyObservedSellers } from "@/components/storefront-hunter/RecentlyObservedSellers";
import {
  DEFAULT_FILTERS,
  applyProductFilters,
  loadColumnPrefs,
  loadStorefrontHunterBundle,
  persistColumnPrefs,
  productsForSeller,
  sortProducts,
} from "@/components/storefront-hunter/models";
import {
  analyzeStorefront,
  enrichStorefrontItems,
  fetchSellerOfferPrices,
  fetchStorefrontProducts,
  mapAnalysisToBundle,
  storefrontErrorMessage,
  fetchSavedProductStatus,
  saveStorefrontProduct,
  unsaveStorefrontProduct,
} from "@/lib/storefrontHunter/api";
import { buildLoadedCatalogFacets } from "@/lib/storefrontHunter/pageFacets";
import {
  isSaved,
  normalizeSavedItemId,
  setSavedFlag,
  withSaving,
} from "@/lib/storefrontHunter/savedProducts";
import { clearMarketCheckCache, emptyMarketCheckEntry } from "@/lib/storefrontHunter/marketCheck";
import {
  STOREFRONT_ENRICH_BATCH_SIZE,
  STOREFRONT_ENRICH_MAX_CONCURRENCY,
  MARKETPLACE_ENRICH_FIELDS,
  chunk,
  createEnrichmentCache,
  isTerminalCacheableEnrichmentPatch,
  finalizeEnrichmentBatch,
  isMarketplaceFresh,
  markRefreshableEnrichment,
  mergeSellerOfferPrices,
  mergeStorefrontProductPatch,
  missingStorefrontPriceIds,
  splitEnrichmentTargets,
  runWithConcurrency as runEnrichConcurrency,
} from "@/lib/storefrontHunter/progressiveEnrich";
import {
  BUY_SOURCES_AUTO_CONCURRENCY,
  BUY_SOURCES_BATCH_MAX_CONCURRENCY,
  BUY_SOURCES_BATCH_SIZE,
  BUY_SOURCES_STATUS,
  buySourceReady,
  chunkBuySourcesBatch,
  classifyBuySourcesResult,
  createAutoSourceTelemetry,
  createBuySourcesCache,
  createConcurrencyGate,
  emptyBuySourcesEntry,
  entryFromParsed,
  pendingBuySourceIds,
  productItemId,
  runWithConcurrency as runBuySourcesConcurrency,
  seedBuySourcesState,
} from "@/lib/storefrontHunter/autoBuySources";
import {
  BUY_SOURCES_FAILURE_REASON,
  buySourcesErrorEntry,
  classifyBuySourcesFailure,
  isProviderUnavailableResult,
  providerUnavailableDetail,
} from "@/lib/storefrontHunter/buySourcesFailures";
import { buildBuySourcesSearchBody } from "@/lib/buySources/buildSearchRequest";
import { parseBuySourcesBatchResponse } from "@/lib/buySources/parseBatchResponse";
import { parseBuySourcesSearchResponse } from "@/lib/buySources/parseSearchResponse";
import { api, unwrapEnvelope } from "@/lib/api";

function countActiveFilters(filters) {
  let n = 0;
  if (filters.productTitle?.trim()) n += 1;
  if (filters.excludeKeywords?.length) n += 1;
  if (filters.upcList?.length || filters.itemIdList?.length || filters.productIds?.trim()) n += 1;
  if (filters.brands?.length) n += 1;
  if (filters.categories?.length) n += 1;
  [
    "storefrontPriceMin",
    "storefrontPriceMax",
    "buyBoxMin",
    "buyBoxMax",
    "maxCostMin",
    "maxCostMax",
    "monthlySoldMin",
    "monthlySoldMax",
    "sellersMin",
    "sellersMax",
    "ratingMin",
    "reviewCountMin",
    "reviewCountMax",
    "newProductAge",
  ].forEach((k) => {
    if (String(filters[k] || "").trim()) n += 1;
  });
  if (filters.newProductsOnly) n += 1;
  if (filters.hasBuySources) n += 1;
  if (filters.availability && filters.availability !== "Any") n += 1;
  if (filters.fulfillment && filters.fulfillment !== "Any") n += 1;
  return n;
}

/** Initial catalog paint timing; read via window.__izisellSfhInitialPaint. */
function recordInitialPaintTiming(startedAt, respondedAt, catalogTiming) {
  if (typeof window === "undefined" || typeof requestAnimationFrame !== "function") return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const paintedAt = performance.now();
      const backendMs = catalogTiming?.catalog_backend_total_ms ?? null;
      const requestMs = Math.round(respondedAt - startedAt);
      window.__izisellSfhInitialPaint = {
        cache_state: catalogTiming?.cache_state ?? null,
        outcome: catalogTiming?.outcome ?? null,
        catalog_queue_ms: catalogTiming?.catalog_queue_ms ?? null,
        catalog_provider_ms: catalogTiming?.catalog_provider_ms ?? null,
        catalog_parse_ms: catalogTiming?.catalog_parse_ms ?? null,
        catalog_transform_ms: catalogTiming?.catalog_transform_ms ?? null,
        catalog_backend_total_ms: backendMs,
        network_overhead_ms: backendMs == null ? null : Math.max(0, requestMs - backendMs),
        frontend_wait_after_response_ms: Math.round(paintedAt - respondedAt),
        initial_paint_total_ms: Math.round(paintedAt - startedAt),
      };
    });
  });
}

function pageCacheKey(sellerId, page, pageSize) {
  return `${sellerId}|${page}|${pageSize}`;
}

const SELLER_OFFER_MAX_CONCURRENCY = 2;
const EMPTY_LOADED_CATALOG = { sellerId: null, byId: {}, pages: [] };

/** Remember brand/category per item across every loaded page of one seller. */
function accumulateLoadedCatalog(prev, sellerId, page, products) {
  const same = prev.sellerId === sellerId;
  const byId = same ? { ...prev.byId } : {};
  for (const p of products || []) {
    const id = productItemId(p);
    if (!id) continue;
    const old = byId[id];
    byId[id] = {
      brand: p.brand || old?.brand || null,
      category: p.category || old?.category || null,
    };
  }
  const pages = same ? prev.pages : [];
  return {
    sellerId,
    byId,
    pages: pages.includes(page) ? pages : [...pages, page],
  };
}

/** @typedef {'idle' | 'loading' | 'success' | 'empty' | 'error'} StorefrontStatus */

export default function StorefrontHunterPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("idle");
  /** Seller identity known before the catalog response (Seller Watch click). */
  const [pendingSeller, setPendingSeller] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [columns, setColumns] = useState(() => loadColumnPrefs());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [watchOpen, setWatchOpen] = useState(false);
  /* Saved products: canonical identity is (workspace, walmart_item_id), so the
     same product found under another seller still reads as saved. */
  const [savedItemIds, setSavedItemIds] = useState(() => new Set());
  const [savingIds, setSavingIds] = useState(() => new Set());
  const savedHydrationRef = useRef(new Set());
  const [watchRefreshKey, setWatchRefreshKey] = useState(0);
  const rootRef = useRef(null);
  const watchDockRef = useRef(null);
  useSellerWatchDock(watchOpen, rootRef, watchDockRef);
  const [selectedSellerId, setSelectedSellerId] = useState(null);
  const [marketChecks, setMarketChecks] = useState({});
  const [buySourcesByItemId, setBuySourcesByItemId] = useState({});
  /** @type {['next'|'previous'|null, Function]} */
  const [pageLoadingDirection, setPageLoadingDirection] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [loadedCatalog, setLoadedCatalog] = useState(EMPTY_LOADED_CATALOG);
  /** Walmart internal seller id — proves offer ownership for Storefront Price recovery. */
  const sellerUuidRef = useRef(null);
  const sellerOfferCacheRef = useRef(new Map());
  const sellerOfferGenerationRef = useRef(0);

  const pageCacheRef = useRef(new Map());
  const pageRequestIdRef = useRef(0);
  const productsTopRef = useRef(null);
  const enrichGenerationRef = useRef(0);
  const enrichmentCacheRef = useRef(createEnrichmentCache());
  /** Page/seller epoch — bump ONLY on analyze or page change, never on Retry. */
  const buySourcesGenerationRef = useRef(0);
  const buySourcesCacheRef = useRef(createBuySourcesCache());
  const buySourcesTelemetryRef = useRef(createAutoSourceTelemetry());
  /** Shared in-flight set so overlapping queues do not double-call the same product. */
  const buySourcesInFlightRef = useRef(new Set());
  /** Item ids currently visible or within the prefetch margin. Keyed by
      normalized Walmart Item ID — never array index or seller+item. */
  const buySourcesEligibleRef = useRef(new Set());
  const buySourcesProductsRef = useRef([]);
  /** Shared concurrency gate — page queue + Retry share BUY_SOURCES_AUTO_CONCURRENCY. */
  const buySourcesGateRef = useRef(createConcurrencyGate(BUY_SOURCES_AUTO_CONCURRENCY));
  const productsByIdRef = useRef(new Map());
  /** Marketplace enrich retries in flight — ignore duplicate Retry clicks. */
  const marketplaceRetryInFlightRef = useRef(new Set());
  /** Latest filters for post-enrich Buy Sources qualification. */
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const analyzeLoading = status === "loading" && !bundle?.store;

  const patchProductsFromEnrichment = useCallback((itemsMap) => {
    if (!itemsMap || typeof itemsMap !== "object") return;
    setBundle((prev) => {
      if (!prev?.products) return prev;
      return {
        ...prev,
        products: mergeStorefrontProductPatch(prev.products, itemsMap),
      };
    });
    // Keep on-demand marketChecks in sync when lightweight enrich succeeds.
    setMarketChecks((prev) => {
      const next = { ...prev };
      for (const [itemId, patch] of Object.entries(itemsMap)) {
        const cur = next[itemId] || emptyMarketCheckEntry();
        const buyBox =
          patch?.buy_box_price != null ||
          (typeof patch?.buy_box_owner === "string" && patch.buy_box_owner.trim())
            ? {
                status: "success",
                price: patch.buy_box_price != null ? patch.buy_box_price : null,
                seller: patch.buy_box_owner || null,
                checkedAt: patch.last_checked || new Date().toISOString(),
              }
            : cur.buyBox;
        const sellers =
          patch?.seller_count != null
            ? {
                status: patch.seller_count === 0 ? "empty" : "success",
                count: patch.seller_count,
                checkedAt: patch.last_checked || new Date().toISOString(),
              }
            : cur.sellers;
        next[itemId] = { buyBox, sellers };
        if (isTerminalCacheableEnrichmentPatch(patch)) {
          enrichmentCacheRef.current.set(itemId, patch);
        }
      }
      return next;
    });
  }, []);

  const runProgressiveEnrichment = useCallback(
    async (products, sellerId) => {
      if (!products?.length || !sellerId) return;
      const generation = ++enrichGenerationRef.current;

      // Apply cache hits immediately so they never sit on Loading… forever.
      const { cachedPatches, fetchIds: allFetchIds } = splitEnrichmentTargets(
        products,
        enrichmentCacheRef.current
      );
      if (Object.keys(cachedPatches).length) {
        patchProductsFromEnrichment(cachedPatches);
      }

      // Freshness gate: a product whose marketplace data is still within the
      // Buy Box window is not refetched. This is what stops a revisit from
      // becoming another 40-product provider fanout.
      const byItemId = new Map();
      for (const p of products || []) {
        const id = productItemId(p);
        if (id && !byItemId.has(id)) byItemId.set(id, p);
      }
      const now = Date.now();
      const fetchIds = allFetchIds.filter((id) => {
        const product = byItemId.get(id);
        return !product || !isMarketplaceFresh(product, now);
      });

      if (!fetchIds.length) return;

      // Only IDs that still need a network fetch become pending — and only for
      // fields that have no usable value yet. Known values stay on screen.
      setBundle((prev) => {
        if (!prev?.products) return prev;
        return {
          ...prev,
          products: markRefreshableEnrichment(
            prev.products,
            MARKETPLACE_ENRICH_FIELDS,
            fetchIds
          ),
        };
      });

      const batches = chunk(fetchIds, STOREFRONT_ENRICH_BATCH_SIZE);
      await runEnrichConcurrency(batches, STOREFRONT_ENRICH_MAX_CONCURRENCY, async (batch) => {
        try {
          const response = await enrichStorefrontItems({
            seller_id: sellerId,
            ...(sellerUuidRef.current ? { seller_uuid: sellerUuidRef.current } : {}),
            item_ids: batch,
            fields: ["buy_box", "seller_count", "brand", "category", "demand", "max_cost"],
          });
          if (generation !== enrichGenerationRef.current) return;
          const data = unwrapEnvelope(response) || response || {};
          const items = data.items || {};
          // Every requested ID must become terminal — missing → unavailable.
          patchProductsFromEnrichment(finalizeEnrichmentBatch(batch, items));
        } catch {
          if (generation !== enrichGenerationRef.current) return;
          patchProductsFromEnrichment(finalizeEnrichmentBatch(batch, {}, { error: true }));
        }
      });
    },
    [patchProductsFromEnrichment]
  );

  const patchSellerOffers = useCallback((offersMap) => {
    if (!offersMap || !Object.keys(offersMap).length) return;
    setBundle((prev) => {
      if (!prev?.products) return prev;
      return { ...prev, products: mergeSellerOfferPrices(prev.products, offersMap) };
    });
  }, []);

  const cachedSellerOffers = useCallback((products) => {
    const hits = {};
    for (const id of missingStorefrontPriceIds(products)) {
      const hit = sellerOfferCacheRef.current.get(id);
      if (hit) hits[id] = hit;
    }
    return hits;
  }, []);

  /**
   * Fill missing Storefront Prices with the analyzed seller's own offer.
   * Runs after the catalog renders, never blocks it, and never replaces a known price.
   */
  const runSellerOfferRecovery = useCallback(
    async (products, sellerId) => {
      const sellerUuid = sellerUuidRef.current;
      if (!products?.length || !sellerId || !sellerUuid) return;
      const generation = sellerOfferGenerationRef.current;
      const cached = cachedSellerOffers(products);
      patchSellerOffers(cached);
      const fetchIds = missingStorefrontPriceIds(products).filter((id) => !cached[id]);
      if (!fetchIds.length) return;
      const batches = chunk(fetchIds, STOREFRONT_ENRICH_BATCH_SIZE);
      await runEnrichConcurrency(batches, SELLER_OFFER_MAX_CONCURRENCY, async (batch) => {
        try {
          const response = await fetchSellerOfferPrices({
            seller_id: sellerId,
            seller_uuid: sellerUuid,
            item_ids: batch,
          });
          if (generation !== sellerOfferGenerationRef.current) return;
          const data = unwrapEnvelope(response) || response || {};
          const items = data.items || {};
          for (const [id, item] of Object.entries(items)) {
            if (item && item.reason !== "seller_offer_fetch_failed") {
              sellerOfferCacheRef.current.set(id, item);
            }
          }
          patchSellerOffers(items);
        } catch {
          // Leave the price unknown; the catalog stays usable without it.
        }
      });
    },
    [cachedSellerOffers, patchSellerOffers]
  );

  const patchBuySourcesEntry = useCallback((itemId, entry) => {
    const id = String(itemId || "").trim();
    if (!id || !entry) return;
    setBuySourcesByItemId((prev) => ({ ...prev, [id]: entry }));
  }, []);

  const runAutoBuySources = useCallback(
    async (products, { forceIds = null, eligibleIds = null } = {}) => {
      if (!products?.length) return;
      const byId = new Map();
      for (const p of products) {
        const id = productItemId(p);
        if (id) byId.set(id, p);
      }
      for (const [id, p] of byId) {
        productsByIdRef.current.set(id, p);
      }
      buySourcesProductsRef.current = products;

      const generation = buySourcesGenerationRef.current;
      const telemetry = buySourcesTelemetryRef.current;
      const seeded = seedBuySourcesState(
        products,
        buySourcesCacheRef.current,
        forceIds?.length ? null : telemetry,
        eligibleIds
      );

      if (forceIds?.length) {
        for (const id of forceIds) {
          seeded[id] = emptyBuySourcesEntry(BUY_SOURCES_STATUS.QUEUED);
          buySourcesCacheRef.current.delete(id);
          buySourcesInFlightRef.current.delete(id);
        }
      }

      setBuySourcesByItemId((prev) => {
        const next = { ...prev };
        for (const id of Object.keys(seeded)) {
          const cur = prev[id]?.status;
          // Do not regress a terminal (complete/no_match/unresolved/error) or
          // in-flight entry when re-seeding after a scroll-eligibility change.
          if (
            !cur ||
            cur === BUY_SOURCES_STATUS.IDLE ||
            cur === BUY_SOURCES_STATUS.QUEUED
          ) {
            next[id] = seeded[id];
          }
        }
        return next;
      });

      const forceSet = forceIds?.length ? new Set(forceIds.map(String)) : null;
      let pending = pendingBuySourceIds(products, seeded, buySourcesCacheRef.current);
      if (forceSet) {
        pending = [...new Set([...pending, ...forceSet])];
      }
      // Viewport gating: only visible/near-visible ids are dispatched. Manual
      // Refresh (forceIds) always bypasses this.
      if (!forceSet && eligibleIds instanceof Set) {
        pending = pending.filter((id) => eligibleIds.has(id));
      }
      pending = pending.filter(
        (id) => forceSet?.has(id) || !buySourcesInFlightRef.current.has(id)
      );
      if (!pending.length) return;

      telemetry.products_visible = Math.max(telemetry.products_visible || 0, products.length);
      telemetry.buy_sources_requested += pending.length;

      // Manual Retry / refresh: keep single-product buySourcesSearch.
      if (forceSet) {
        telemetry.max_concurrency = BUY_SOURCES_AUTO_CONCURRENCY;
        await runBuySourcesConcurrency(
          pending,
          BUY_SOURCES_AUTO_CONCURRENCY,
          async (itemId) => {
            if (generation !== buySourcesGenerationRef.current) return;
            const product = productsByIdRef.current.get(itemId);
            if (!product) return;
            if (buySourcesInFlightRef.current.has(itemId) && !forceSet.has(itemId)) return;

            const queueWaitMs = buySourcesGateRef.current?.lastQueueWaitMs ?? 0;
            buySourcesInFlightRef.current.add(itemId);
            patchBuySourcesEntry(itemId, emptyBuySourcesEntry(BUY_SOURCES_STATUS.SEARCHING));
            telemetry.auto_source_requests_started += 1;
            telemetry.buy_sources_attempted += 1;

            try {
              const body = buildBuySourcesSearchBody(product);
              if (!body.title) {
                if (generation !== buySourcesGenerationRef.current) return;
                const classification = classifyBuySourcesFailure(null, { missingTitle: true });
                const errEntry = buySourcesErrorEntry(
                  emptyBuySourcesEntry(BUY_SOURCES_STATUS.ERROR),
                  { ...classification, queue_wait_ms: queueWaitMs }
                );
                patchBuySourcesEntry(itemId, errEntry);
                telemetry.auto_source_errors += 1;
                telemetry.buy_sources_failed += 1;
                const reason = classification.reason;
                telemetry.failure_reasons[reason] = (telemetry.failure_reasons[reason] || 0) + 1;
                return;
              }

              const providerStarted = Date.now();
              const res = await api.buySourcesSearch(body);
              const clientProviderElapsedMs = Date.now() - providerStarted;
              if (generation !== buySourcesGenerationRef.current) {
                try {
                  const parsed = parseBuySourcesSearchResponse(res);
                  const status = classifyBuySourcesResult(parsed);
                  buySourcesCacheRef.current.set(itemId, entryFromParsed(parsed, status));
                } catch {
                  /* ignore */
                }
                return;
              }

              const parsed = parseBuySourcesSearchResponse(res);
              if (isProviderUnavailableResult(parsed)) {
                const unavailable = new Error("Source search unavailable");
                unavailable.response = { data: { detail: providerUnavailableDetail(parsed) } };
                throw unavailable;
              }
              const status = classifyBuySourcesResult(parsed);
              const providerElapsedMs =
                parsed.provider_elapsed_ms ?? clientProviderElapsedMs;
              const totalElapsedMs =
                parsed.total_elapsed_ms ??
                parsed.elapsed_ms ??
                queueWaitMs + providerElapsedMs;
              const entry = {
                ...entryFromParsed(parsed, status),
                request_id: parsed.request_id || null,
                diagnostics: {
                  buy_sources_attempted: true,
                  buy_sources_succeeded: true,
                  buy_sources_failed: false,
                  buy_sources_failure_reason: null,
                  provider_calls: parsed.provider_calls ?? 1,
                  request_id: parsed.request_id || null,
                  walmart_item_id: parsed.walmart_item_id || itemId,
                  elapsed_ms: parsed.elapsed_ms ?? totalElapsedMs,
                  provider_elapsed_ms: providerElapsedMs,
                  total_elapsed_ms: totalElapsedMs,
                  queue_wait_ms: queueWaitMs,
                  provider_status_class: parsed.provider_status_class || "ok",
                  result_count: parsed.result_count ?? null,
                },
              };
              buySourcesCacheRef.current.set(itemId, entry);
              patchBuySourcesEntry(itemId, entry);
              telemetry.buy_sources_succeeded += 1;
              telemetry.buy_sources_completed += 1;
              if (status === BUY_SOURCES_STATUS.COMPLETE) telemetry.auto_source_completed += 1;
              else if (status === BUY_SOURCES_STATUS.NO_MATCH) telemetry.auto_source_no_match += 1;
              else if (status === BUY_SOURCES_STATUS.UNRESOLVED) telemetry.auto_source_completed += 1;
            } catch (err) {
              const classification = classifyBuySourcesFailure(err);
              if (generation !== buySourcesGenerationRef.current) return;
              if (classification.reason === BUY_SOURCES_FAILURE_REASON.FRONTEND_ABORTED) {
                return;
              }
              const errEntry = {
                ...buySourcesErrorEntry(
                  emptyBuySourcesEntry(BUY_SOURCES_STATUS.ERROR),
                  {
                    ...classification,
                    walmart_item_id: classification.walmart_item_id || itemId,
                    queue_wait_ms: classification.queue_wait_ms ?? queueWaitMs,
                  }
                ),
              };
              patchBuySourcesEntry(itemId, errEntry);
              telemetry.auto_source_errors += 1;
              telemetry.buy_sources_failed += 1;
              telemetry.buy_sources_completed += 1;
              const reason = classification.reason;
              telemetry.failure_reasons[reason] = (telemetry.failure_reasons[reason] || 0) + 1;
            } finally {
              buySourcesInFlightRef.current.delete(itemId);
            }
          },
          buySourcesGateRef.current
        );
        return;
      }

      // Phase 3 auto-source: chunk into search-batch HTTP calls.
      telemetry.max_concurrency = BUY_SOURCES_BATCH_MAX_CONCURRENCY;
      const batches = chunkBuySourcesBatch(pending, BUY_SOURCES_BATCH_SIZE);

      await runBuySourcesConcurrency(
        batches,
        BUY_SOURCES_BATCH_MAX_CONCURRENCY,
        async (batchIds) => {
          if (generation !== buySourcesGenerationRef.current) return;

          const items = [];
          const readyIds = [];
          for (const itemId of batchIds) {
            if (generation !== buySourcesGenerationRef.current) return;
            if (buySourcesInFlightRef.current.has(itemId)) continue;
            const product = productsByIdRef.current.get(itemId);
            if (!product) continue;
            const body = buildBuySourcesSearchBody(product);
            if (!body.title) {
              const classification = classifyBuySourcesFailure(null, { missingTitle: true });
              const errEntry = buySourcesErrorEntry(
                emptyBuySourcesEntry(BUY_SOURCES_STATUS.ERROR),
                classification
              );
              patchBuySourcesEntry(itemId, errEntry);
              telemetry.auto_source_errors += 1;
              telemetry.buy_sources_failed += 1;
              telemetry.buy_sources_completed += 1;
              const reason = classification.reason;
              telemetry.failure_reasons[reason] = (telemetry.failure_reasons[reason] || 0) + 1;
              continue;
            }
            buySourcesInFlightRef.current.add(itemId);
            patchBuySourcesEntry(itemId, emptyBuySourcesEntry(BUY_SOURCES_STATUS.SEARCHING));
            readyIds.push(itemId);
            items.push(body);
          }

          if (!items.length) return;
          if (generation !== buySourcesGenerationRef.current) {
            for (const id of readyIds) buySourcesInFlightRef.current.delete(id);
            return;
          }

          telemetry.auto_source_requests_started += 1;
          telemetry.buy_sources_attempted += items.length;
          const queueWaitMs = buySourcesGateRef.current?.lastQueueWaitMs ?? 0;
          const providerStarted = Date.now();

          try {
            const res = await api.buySourcesSearchBatch({ items });
            const clientElapsed = Date.now() - providerStarted;
            if (generation !== buySourcesGenerationRef.current) {
              // Stale: cache successes for return visits; do not patch active page.
              try {
                const batch = parseBuySourcesBatchResponse(res);
                for (const row of batch.results) {
                  const id = row.walmart_item_id;
                  if (!id || row.status === BUY_SOURCES_STATUS.ERROR || !row.parsed) continue;
                  buySourcesCacheRef.current.set(
                    id,
                    entryFromParsed(row.parsed, row.status)
                  );
                }
              } catch {
                /* ignore */
              }
              return;
            }

            const batch = parseBuySourcesBatchResponse(res);
            const seen = new Set();
            for (const row of batch.results) {
              const itemId = row.walmart_item_id;
              if (!itemId) continue;
              seen.add(itemId);
              if (row.status === BUY_SOURCES_STATUS.ERROR || !row.parsed) {
                const classification = classifyBuySourcesFailure(
                  row.error
                    ? { response: { data: { detail: row.error } } }
                    : null
                );
                const errEntry = buySourcesErrorEntry(
                  emptyBuySourcesEntry(BUY_SOURCES_STATUS.ERROR),
                  {
                    ...classification,
                    walmart_item_id: itemId,
                    queue_wait_ms: queueWaitMs,
                  }
                );
                patchBuySourcesEntry(itemId, errEntry);
                telemetry.auto_source_errors += 1;
                telemetry.buy_sources_failed += 1;
                telemetry.buy_sources_completed += 1;
                const reason = classification.reason;
                telemetry.failure_reasons[reason] = (telemetry.failure_reasons[reason] || 0) + 1;
                continue;
              }

              const entry = {
                ...entryFromParsed(row.parsed, row.status),
                request_id: row.parsed.request_id || null,
                diagnostics: {
                  buy_sources_attempted: true,
                  buy_sources_succeeded: true,
                  buy_sources_failed: false,
                  buy_sources_failure_reason: null,
                  provider_calls: row.parsed.provider_calls ?? 1,
                  request_id: row.parsed.request_id || null,
                  walmart_item_id: itemId,
                  elapsed_ms: row.parsed.elapsed_ms ?? clientElapsed,
                  provider_elapsed_ms: row.parsed.provider_elapsed_ms ?? clientElapsed,
                  total_elapsed_ms: row.parsed.total_elapsed_ms ?? clientElapsed,
                  queue_wait_ms: queueWaitMs,
                  provider_status_class: row.parsed.provider_status_class || "ok",
                  result_count: row.parsed.result_count ?? null,
                },
              };
              buySourcesCacheRef.current.set(itemId, entry);
              patchBuySourcesEntry(itemId, entry);
              telemetry.buy_sources_succeeded += 1;
              telemetry.buy_sources_completed += 1;
              if (row.status === BUY_SOURCES_STATUS.COMPLETE) telemetry.auto_source_completed += 1;
              else if (row.status === BUY_SOURCES_STATUS.NO_MATCH) telemetry.auto_source_no_match += 1;
              else if (row.status === BUY_SOURCES_STATUS.UNRESOLVED) telemetry.auto_source_completed += 1;
            }

            // Any requested id missing from batch → error (should not happen).
            for (const itemId of readyIds) {
              if (seen.has(itemId)) continue;
              if (generation !== buySourcesGenerationRef.current) continue;
              const errEntry = buySourcesErrorEntry(
                emptyBuySourcesEntry(BUY_SOURCES_STATUS.ERROR),
                {
                  reason: BUY_SOURCES_FAILURE_REASON.BACKEND_ERROR,
                  walmart_item_id: itemId,
                  queue_wait_ms: queueWaitMs,
                }
              );
              patchBuySourcesEntry(itemId, errEntry);
              telemetry.auto_source_errors += 1;
              telemetry.buy_sources_failed += 1;
              telemetry.buy_sources_completed += 1;
            }
          } catch (err) {
            const classification = classifyBuySourcesFailure(err);
            if (generation !== buySourcesGenerationRef.current) return;
            if (classification.reason === BUY_SOURCES_FAILURE_REASON.FRONTEND_ABORTED) {
              return;
            }
            for (const itemId of readyIds) {
              const errEntry = buySourcesErrorEntry(
                emptyBuySourcesEntry(BUY_SOURCES_STATUS.ERROR),
                {
                  ...classification,
                  walmart_item_id: classification.walmart_item_id || itemId,
                  queue_wait_ms: classification.queue_wait_ms ?? queueWaitMs,
                }
              );
              patchBuySourcesEntry(itemId, errEntry);
              telemetry.auto_source_errors += 1;
              telemetry.buy_sources_failed += 1;
              telemetry.buy_sources_completed += 1;
              const reason = classification.reason;
              telemetry.failure_reasons[reason] = (telemetry.failure_reasons[reason] || 0) + 1;
            }
          } finally {
            for (const itemId of readyIds) {
              buySourcesInFlightRef.current.delete(itemId);
            }
          }
        },
        buySourcesGateRef.current
      );
    },
    [patchBuySourcesEntry]
  );

  /**
   * Page-owned secondary work: marketplace enrich FIRST, then qualified Buy Sources.
   * Never launches enrich + buy-sources in uncontrolled parallel.
   * Phase 3: auto Buy Sources uses search-batch chunks after qualification;
   * manual Retry still uses single-product buySourcesSearch.
   */
  const runPageSecondaryWork = useCallback(
    (products, sellerId) => {
      if (!products?.length || !sellerId) return;

      // Independent secondary workloads. Buy Sources is intentionally NOT
      // started from this page-wide pipeline anymore. The viewport effect owns
      // source discovery per ready product, so marketplace enrichment cannot
      // delay the first Buy Sources request.
      void runSellerOfferRecovery(products, sellerId);
      void runProgressiveEnrichment(products, sellerId);
    },
    [runProgressiveEnrichment, runSellerOfferRecovery]
  );

  const onRetryBuySources = useCallback(
    (product) => {
      const id = productItemId(product);
      if (!id) return;
      void runAutoBuySources([product], { forceIds: [id] });
    },
    [runAutoBuySources]
  );

  const onRefreshBuySources = useCallback(
    (product) => {
      const id = productItemId(product);
      if (!id) return;
      void runAutoBuySources([product], { forceIds: [id] });
    },
    [runAutoBuySources]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await loadStorefrontHunterBundle();
        if (cancelled) return;
        if (data.dataSource === "temporary_fixture") {
          setBundle(data);
          setSelectedSellerId(data.sellers?.[0]?.seller_id || data.store?.seller_id || null);
          setStatus("success");
        } else {
          setBundle(data);
          setStatus("idle");
        }
      } catch {
        if (!cancelled) setStatus("idle");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Accepts an explicit seller (Seller Watch selection); a DOM event is ignored.
  const runAnalyze = useCallback(async (override, meta) => {
    const requested = typeof override === "string" ? override : query;
    const seller = String(requested || "").trim();
    if (!seller) {
      toast.error("Paste a Walmart seller URL or enter a Seller ID");
      return;
    }
    // Seller Watch click: show its known identity immediately, then skeleton the
    // rest. Never the previous seller's identity/data.
    setPendingSeller(
      meta && typeof meta === "object" && meta.seller_id
        ? {
            seller_id: String(meta.seller_id),
            seller_name: meta.seller_name ?? null,
            rating: meta.rating ?? null,
            review_count: meta.review_count ?? null,
            product_count: meta.product_count ?? null,
          }
        : /^\d{4,20}$/.test(seller)
          ? {
              seller_id: seller,
              seller_name: null,
              rating: null,
              review_count: null,
              product_count: null,
            }
          : null
    );
    setStatus("loading");
    setErrorMessage(null);
    setMarketChecks({});
    setBuySourcesByItemId({});
    clearMarketCheckCache();
    pageCacheRef.current.clear();
    enrichmentCacheRef.current.clear();
    buySourcesCacheRef.current.clear();
    buySourcesTelemetryRef.current = createAutoSourceTelemetry();
    buySourcesInFlightRef.current.clear();
    buySourcesEligibleRef.current.clear();
    buySourcesProductsRef.current = [];
    enrichGenerationRef.current += 1;
    buySourcesGenerationRef.current += 1;
    sellerOfferGenerationRef.current += 1;
    sellerOfferCacheRef.current.clear();
    sellerUuidRef.current = null;
    setLoadedCatalog(EMPTY_LOADED_CATALOG);
    pageRequestIdRef.current += 1;
    setPageLoadingDirection(null);
    setProductsLoading(false);
    const startedAt = performance.now();
    try {
      const analysis = await analyzeStorefront(seller, { page: 1, pageSize: 40 });
      const respondedAt = performance.now();
      recordInitialPaintTiming(startedAt, respondedAt, analysis?.catalog_timing);
      const mapped = mapAnalysisToBundle(analysis);
      sellerUuidRef.current = mapped.store?.seller_uuid || null;
      setBundle(mapped);
      setPage(1);
      setSelectedSellerId(mapped.store?.seller_id || null);
      const sid = mapped.store?.seller_id;
      if (sid) {
        pageCacheRef.current.set(pageCacheKey(sid, 1, mapped.pagination?.page_size ?? 40), {
          products: mapped.products,
          pagination: mapped.pagination,
        });
      }
      if (mapped.catalog_status === "empty" || !(mapped.products || []).length) {
        setStatus("empty");
      } else {
        setStatus("success");
        setBuySourcesByItemId({});
        // Progressive marketplace enrich first; Buy Sources only after settle + qualify.
        void runPageSecondaryWork(mapped.products, sid);
      }
      // A real catalog check also records a Seller Watch observation server-side.
      setWatchRefreshKey((k) => k + 1);
      setPendingSeller(null);
    } catch (err) {
      const msg = storefrontErrorMessage(err);
      setErrorMessage(msg);
      setPendingSeller(null);
      setStatus("error");
      setBundle(null);
      toast.error(msg);
    }
  }, [query, runPageSecondaryWork]);

  const applyPagePayload = useCallback((pagePayload, nextPage, sellerId) => {
    setBundle((prev) => {
      if (!prev) return prev;
      const merged = mapAnalysisToBundle({
        seller: {
          ...prev.store,
          seller_uuid: prev.store?.seller_uuid || pagePayload.seller_uuid || null,
        },
        top_brands: prev.brands,
        top_categories: prev.categories,
        catalog_facets: pagePayload.catalog_facets || prev.catalogFacets,
        products: pagePayload.products,
        pagination: pagePayload.pagination,
        catalog_status: pagePayload.catalog_status,
        catalog_scope: prev.catalog_scope,
        catalog_complete: prev.catalog_complete,
        product_source: prev.product_source,
      });
      return merged;
    });
    setPage(nextPage);
    pageCacheRef.current.set(
      pageCacheKey(sellerId, nextPage, pagePayload.pagination?.page_size ?? 40),
      {
        products: pagePayload.products,
        pagination: pagePayload.pagination,
      }
    );
  }, []);

  const loadPage = useCallback(
    async (nextPage, direction) => {
      const sellerId = selectedSellerId || bundle?.store?.seller_id;
      if (!sellerId || bundle?.dataSource !== "api") return;
      const pageSize = bundle?.pagination?.page_size ?? 40;
      const cacheKey = pageCacheKey(sellerId, nextPage, pageSize);
      const cached = pageCacheRef.current.get(cacheKey);

      if (cached) {
        // Abort prior page's in-flight workers; do not treat as permanent failure.
        buySourcesGenerationRef.current += 1;
        buySourcesInFlightRef.current.clear();
        buySourcesEligibleRef.current.clear();
        // Re-apply any fresh enrichment / seller-offer cache onto cached discovery products.
        const enriched = mergeStorefrontProductPatch(
          cached.products,
          Object.fromEntries(
            (cached.products || [])
              .map((p) => {
                const id = String(p?.walmart_item_id || "");
                const hit = id ? enrichmentCacheRef.current.get(id) : null;
                return hit ? [id, hit] : null;
              })
              .filter(Boolean)
          )
        );
        const patched = mergeSellerOfferPrices(enriched, cachedSellerOffers(enriched));
        setBundle((prev) =>
          prev
            ? {
                ...prev,
                products: patched,
                pagination: cached.pagination,
              }
            : prev
        );
        setPage(nextPage);
        setBuySourcesByItemId((prev) => {
          const next = { ...prev };
          // Reset visible page keys to idle until post-enrich qualification runs.
          for (const p of patched || []) {
            const id = productItemId(p);
            if (id) next[id] = emptyBuySourcesEntry(BUY_SOURCES_STATUS.IDLE);
          }
          return next;
        });
        requestAnimationFrame(() => {
          productsTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
        void runPageSecondaryWork(patched, sellerId);
        return;
      }

      const reqId = ++pageRequestIdRef.current;
      enrichGenerationRef.current += 1;
      buySourcesGenerationRef.current += 1;
      buySourcesInFlightRef.current.clear();
      buySourcesEligibleRef.current.clear();
      buySourcesProductsRef.current = [];
      setPageLoadingDirection(direction);
      setProductsLoading(true);
      setErrorMessage(null);

      try {
        const pagePayload = await fetchStorefrontProducts(sellerId, {
          page: nextPage,
          pageSize,
        });
        if (reqId !== pageRequestIdRef.current) return;

        applyPagePayload(pagePayload, nextPage, sellerId);
        setStatus(
          pagePayload.catalog_status === "empty" || !(pagePayload.products || []).length
            ? "empty"
            : "success"
        );
        requestAnimationFrame(() => {
          productsTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
        const mappedProducts = mapAnalysisToBundle({
          seller: { seller_id: sellerId },
          products: pagePayload.products || [],
          pagination: pagePayload.pagination,
        }).products;
        void runPageSecondaryWork(mappedProducts, sellerId);
      } catch (err) {
        if (reqId !== pageRequestIdRef.current) return;
        const msg = storefrontErrorMessage(err);
        setErrorMessage(msg);
        toast.error(msg);
      } finally {
        if (reqId === pageRequestIdRef.current) {
          setPageLoadingDirection(null);
          setProductsLoading(false);
        }
      }
    },
    [selectedSellerId, bundle, applyPagePayload, runPageSecondaryWork, cachedSellerOffers]
  );

  const selectedStore = useMemo(() => {
    if (!bundle) return null;
    return bundle.storesBySeller?.[selectedSellerId] || bundle.store;
  }, [bundle, selectedSellerId]);

  const scopedProducts = useMemo(
    () => productsForSeller(bundle?.products, selectedSellerId),
    [bundle, selectedSellerId]
  );

  useEffect(() => {
    if (!selectedSellerId) return;
    setLoadedCatalog((prev) =>
      accumulateLoadedCatalog(prev, selectedSellerId, page, scopedProducts)
    );
  }, [scopedProducts, selectedSellerId, page]);

  const pageFacets = useMemo(() => {
    const sameSeller = loadedCatalog.sellerId === selectedSellerId;
    const loaded = sameSeller ? Object.values(loadedCatalog.byId) : scopedProducts;
    const featured = bundle?.catalog_scope === "featured";
    return buildLoadedCatalogFacets(loaded, {
      catalogTotal: featured ? null : bundle?.pagination?.total ?? selectedStore?.product_count ?? null,
      pagesLoaded: sameSeller ? Math.max(1, loadedCatalog.pages.length) : 1,
      catalogFacets: bundle?.catalogFacets || null,
    });
  }, [loadedCatalog, selectedSellerId, scopedProducts, bundle, selectedStore]);

  const categoryState = useMemo(() => {
    if (pageFacets.categories.length > 0) return "complete";
    const pending = (scopedProducts || []).some((p) => {
      const st = p?.enrichment?.category;
      return st === "pending" || st === "loading";
    });
    if (pending) return "loading";
    return "unavailable";
  }, [scopedProducts, pageFacets.categories]);

  const brandState = useMemo(() => {
    if (pageFacets.brands.length > 0) return "complete";
    // Fall back to analyze seller facets when page product brands are empty
    // (featured SSR often omits per-product brand; Universal enrich may too).
    const analyzeBrands = (bundle?.brands || []).filter((b) => b?.name);
    if (analyzeBrands.length > 0) return "complete";
    const pending = (scopedProducts || []).some((p) => {
      const st = p?.enrichment?.brand;
      return st === "pending" || st === "loading";
    });
    if (pending) return "loading";
    return "unavailable";
  }, [scopedProducts, pageFacets.brands, bundle?.brands]);

  const displayBrands = useMemo(() => {
    if (pageFacets.brands.length > 0) return pageFacets.brands;
    // Map analyze top_brands ({name, product_count}) → page facet shape.
    return (bundle?.brands || [])
      .filter((b) => b?.name)
      .map((b) => ({
        name: b.name,
        count: b.count ?? b.product_count ?? null,
      }));
  }, [pageFacets.brands, bundle?.brands]);

  const brandOptions = useMemo(
    () => displayBrands.map((b) => b.name).filter(Boolean),
    [displayBrands]
  );
  const categoryOptions = useMemo(
    () => pageFacets.categories.map((c) => c.name).filter(Boolean),
    [pageFacets]
  );

  /* One batch request per distinct set of visible ids — never one per card. */
  useEffect(() => {
    // Normalized ids only, so lookup matches what the backend returns.
    const ids = Array.from(
      new Set(
        (scopedProducts || [])
          .map((p) => normalizeSavedItemId(p?.walmart_item_id))
          .filter(Boolean)
      )
    );
    const unseen = ids.filter((id) => !savedHydrationRef.current.has(id));
    if (!unseen.length) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchSavedProductStatus(unseen);
        const saved = (unwrapEnvelope(res) || res || {}).saved || {};
        if (cancelled) return;
        unseen.forEach((id) => savedHydrationRef.current.add(id));
        setSavedItemIds((prev) => {
          let next = prev;
          for (const [id, flag] of Object.entries(saved)) {
            next = setSavedFlag(next, id, Boolean(flag));
          }
          return next;
        });
      } catch {
        /* Hydration failure must never break the catalog view. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scopedProducts]);

  const onToggleSave = useCallback(
    async (product, nextSaved) => {
      // Keyed by the normalized Walmart Item ID — never by seller or index.
      const itemId = normalizeSavedItemId(product?.walmart_item_id);
      if (!itemId || savingIds.has(itemId)) return;
      // Optimistic; rollback on failure. No provider work, no reload.
      const apply = (on) => setSavedItemIds((prev) => setSavedFlag(prev, itemId, on));
      setSavingIds((prev) => withSaving(prev, itemId, true));
      apply(nextSaved);
      savedHydrationRef.current.add(itemId);
      try {
        if (nextSaved) {
          const store = selectedStore || {};
          await saveStorefrontProduct({
            walmart_item_id: itemId,
            title: product?.title ?? null,
            image_url: product?.image_url ?? null,
            brand: product?.brand ?? null,
            category: product?.category ?? null,
            seller_id: store.seller_id ?? selectedSellerId ?? null,
            seller_name: store.seller_name ?? null,
            storefront_price:
              typeof product?.storefront_price === "number" ? product.storefront_price : null,
            buy_box_price:
              typeof product?.buy_box_price === "number" ? product.buy_box_price : null,
          });
          toast.success("Product saved");
        } else {
          await unsaveStorefrontProduct(itemId);
          toast.success("Product unsaved");
        }
      } catch {
        apply(!nextSaved);
        toast.error(nextSaved ? "Couldn't save product" : "Couldn't unsave product");
      } finally {
        setSavingIds((prev) => withSaving(prev, itemId, false));
      }
    },
    [savingIds, selectedStore, selectedSellerId]
  );

  const products = useMemo(() => {
    const filtered = applyProductFilters(scopedProducts, filters);
    return sortProducts(filtered, "recent");
  }, [scopedProducts, filters]);

  /* Viewport-gated Buy Sources.
     Buy Sources is driven directly by the settled rendered product list — never
     by completion of page-wide marketplace enrichment. */
  useEffect(() => {
    const readyProducts = (products || []).filter(buySourceReady);
    if (!readyProducts.length) return undefined;

    const readyIds = new Set(
      readyProducts.map((p) => productItemId(p)).filter(Boolean)
    );

    if (typeof IntersectionObserver === "undefined") {
      // Test/runtime fallback only. Production browsers use IntersectionObserver.
      buySourcesEligibleRef.current = new Set(readyIds);
      void runAutoBuySources(readyProducts, {
        eligibleIds: buySourcesEligibleRef.current,
      });
      return undefined;
    }

    let disposed = false;
    let io = null;
    let raf1 = null;
    let raf2 = null;

    // React has committed the cards, but their initial geometry can still be
    // transient. Wait for two browser layout/paint frames before observing so
    // temporary pre-layout positions cannot activate the whole 40-product page.
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (disposed) return;

        const els = document.querySelectorAll("[data-sfh-item-id]");
        if (!els.length) return;

        const observed = new Set();

        io = new IntersectionObserver(
          (entries) => {
            if (disposed) return;

            let changed = false;

            for (const entry of entries) {
              if (!entry.isIntersecting) continue;

              const id = String(
                entry.target.getAttribute("data-sfh-item-id") || ""
              ).trim();

              if (!id || !readyIds.has(id)) continue;

              if (!buySourcesEligibleRef.current.has(id)) {
                buySourcesEligibleRef.current.add(id);
                changed = true;
              }
            }

            if (changed) {
              void runAutoBuySources(readyProducts, {
                eligibleIds: buySourcesEligibleRef.current,
              });
            }
          },
          {
            root: null,
            rootMargin: "0px 0px 500px 0px",
          }
        );

        els.forEach((el) => {
          const id = String(
            el.getAttribute("data-sfh-item-id") || ""
          ).trim();

          if (!id || !readyIds.has(id) || observed.has(id)) return;

          observed.add(id);
          io.observe(el);
        });
      });
    });

    return () => {
      disposed = true;
      if (raf1 != null) cancelAnimationFrame(raf1);
      if (raf2 != null) cancelAnimationFrame(raf2);
      if (io) io.disconnect();
    };
  }, [products, runAutoBuySources]);

  const onAnalyzeProduct = (product) => {
    const itemId = product?.walmart_item_id;
    if (itemId) {
      navigate(`/app/analyzer?itemId=${encodeURIComponent(itemId)}`);
      return;
    }
    toast.message("Open Product Analyzer when Walmart item ID is available");
  };

  const patchMarketCheck = useCallback((itemId, patch) => {
    const id = String(itemId || "").trim();
    if (!id) return;
    setMarketChecks((prev) => {
      const cur = prev[id] || emptyMarketCheckEntry();
      return {
        ...prev,
        [id]: {
          buyBox: patch.buyBox ? { ...cur.buyBox, ...patch.buyBox } : cur.buyBox,
          sellers: patch.sellers ? { ...cur.sellers, ...patch.sellers } : cur.sellers,
        },
      };
    });
  }, []);

  const onRetryMarketplaceEnrich = useCallback(
    async (itemId) => {
      const id = String(itemId || "").trim();
      const sellerId = selectedSellerId || bundle?.store?.seller_id;
      if (!id || !sellerId) return;
      if (marketplaceRetryInFlightRef.current.has(id)) return;
      marketplaceRetryInFlightRef.current.add(id);

      // Mark only unresolved fields pending; a known value keeps rendering.
      setBundle((prev) => {
        if (!prev?.products) return prev;
        return {
          ...prev,
          products: markRefreshableEnrichment(
            prev.products,
            MARKETPLACE_ENRICH_FIELDS,
            [id]
          ),
        };
      });
      // Refresh the marketplace data for this product without blanking a known
      // value: the loading flag is only set when nothing valid is on screen.
      const current = productsByIdRef.current.get(id);
      const hasKnownBuyBox =
        (current?.buy_box_price != null && Number.isFinite(Number(current.buy_box_price))) ||
        (typeof current?.buy_box_owner === "string" && current.buy_box_owner.trim() !== "");
      const hasKnownSellers =
        typeof current?.seller_count === "number" && Number.isFinite(current.seller_count);
      patchMarketCheck(id, {
        buyBox: hasKnownBuyBox
          ? { status: "success", price: current.buy_box_price ?? null, seller: current.buy_box_owner || null }
          : { status: "loading", price: null, seller: null, checkedAt: null },
        sellers: hasKnownSellers
          ? { status: "success", count: current.seller_count, checkedAt: null }
          : { status: "loading", count: null, checkedAt: null },
      });

      try {
        // Same SFH enrich path as progressive batches — shares process-wide
        // marketplace provider pool. One Universal call refreshes Buy Box + Sellers.
        const response = await enrichStorefrontItems({
          seller_id: sellerId,
          item_ids: [id],
          fields: ["buy_box", "seller_count", "brand", "category", "demand", "max_cost"],
        });
        const data = unwrapEnvelope(response) || response || {};
        const items = data.items || {};
        patchProductsFromEnrichment(finalizeEnrichmentBatch([id], items));
      } catch {
        patchProductsFromEnrichment(finalizeEnrichmentBatch([id], {}, { error: true }));
        patchMarketCheck(id, {
          buyBox: { status: "error", price: null, seller: null, checkedAt: null },
          sellers: { status: "error", count: null, checkedAt: null },
        });
      } finally {
        marketplaceRetryInFlightRef.current.delete(id);
      }
    },
    [selectedSellerId, bundle, patchMarketCheck, patchProductsFromEnrichment]
  );

  const onCheckBuyBox = useCallback(
    (itemId) => {
      void onRetryMarketplaceEnrich(itemId);
    },
    [onRetryMarketplaceEnrich]
  );

  const onCheckSellers = useCallback(
    (itemId) => {
      void onRetryMarketplaceEnrich(itemId);
    },
    [onRetryMarketplaceEnrich]
  );

  const pagination = bundle?.pagination;
  const catalogScope = bundle?.catalog_scope ?? null;
  const catalogComplete = bundle?.catalog_complete === true;
  const totalCount =
    catalogScope === "featured"
      ? null
      : selectedStore?.product_count ?? pagination?.total ?? null;
  const showResults =
    bundle &&
    (status === "success" || status === "empty" || bundle.dataSource === "temporary_fixture");

  /* ── Seller-switch loading ────────────────────────────────────────────────
     First load or a different seller: render the real shell (Store Details /
     Top Brands / Top Categories / "Products from this seller") plus skeletons.
     Same-seller refresh keeps the existing valid data (stale-while-refresh). */
  const loadingShell = status === "loading";
  const switchingSeller =
    loadingShell && !!pendingSeller && pendingSeller.seller_id !== selectedStore?.seller_id;
  const preservingCurrentData =
    loadingShell && !switchingSeller && !showResults && products.length > 0;
  const showWorkspace = Boolean(showResults || loadingShell);
  const showProductSkeletons = loadingShell && !preservingCurrentData && !showResults;
  const shellStore = switchingSeller
    ? pendingSeller
    : loadingShell
      ? selectedStore || pendingSeller
      : selectedStore;
  const shellBrands = switchingSeller ? [] : displayBrands;
  const shellBrandsState = switchingSeller ? "loading" : brandState;
  const shellCategoriesState = switchingSeller ? "loading" : categoryState;
  const shellProducts = preservingCurrentData ? products : showResults ? products : [];

  return (
    <div
      ref={rootRef}
      className={`sfh-root${watchOpen ? " sfh-root--watch-open" : ""}`}
      data-testid="storefront-hunter-page"
    >
      <div className="sfh-page">
        <StorefrontPageHeader />

        {bundle?.dataSource === "temporary_fixture" ? (
          <div className="sfh-fixture-banner" data-testid="sfh-fixture-banner">
            Demo fixture data (?demo=1) — not live Walmart results. No Buy Sources provider searches on
            render.
          </div>
        ) : null}

        <StorefrontSearchPanel
          query={query}
          onQueryChange={setQuery}
          onAnalyze={runAnalyze}
          onOpenFilters={() => setFiltersOpen(true)}
          filterActiveCount={countActiveFilters(filters)}
          loading={analyzeLoading}
        />

        {status === "idle" && !bundle?.store ? (
          <RecentlyObservedSellers
            onAnalyze={(seller) => {
              if (!seller?.seller_id) return;
              setQuery(seller.seller_id);
              void runAnalyze(seller.seller_id, seller);
            }}
            onTracked={() => setWatchRefreshKey((k) => k + 1)}
          />
        ) : null}

        {analyzeLoading && !showWorkspace ? (
          <div className="py-6 text-center text-[12px] text-[#66758a]" data-testid="sfh-loading">
            Loading storefront…
          </div>
        ) : null}

        {status === "error" && errorMessage ? (
          <div
            className="py-6 text-center text-[12px] text-[#b42318]"
            data-testid="sfh-error"
            role="alert"
          >
            {errorMessage}
          </div>
        ) : null}

        {status === "idle" && !bundle?.store ? (
          <div className="py-12 text-center text-[12px] text-[#66758a]" data-testid="sfh-idle">
            Paste a Walmart seller URL or Seller ID, then click Analyze.
          </div>
        ) : null}

        {showWorkspace ? (
          <>
            <StoreSummary
              store={shellStore || (loadingShell ? {} : null)}
              brands={shellBrands}
              categories={switchingSeller ? [] : pageFacets.categories}
              brandsState={shellBrandsState}
              categoriesState={shellCategoriesState}
              facetScope={switchingSeller ? { label: "This Page" } : pageFacets.scope}
              storeWideBrands={switchingSeller ? null : pageFacets.storeWideBrands}
            />
            <div ref={productsTopRef}>
              <StorefrontResultsHeader
                visibleCount={shellProducts.length}
                totalCount={switchingSeller ? null : totalCount}
                catalogScope={switchingSeller ? null : catalogScope}
                catalogComplete={switchingSeller ? false : catalogComplete}
              />
            </div>
            <section
              className={`sfh-product-list${productsLoading ? " sfh-product-list--loading" : ""}`}
              data-testid="sfh-product-list"
              aria-busy={productsLoading || showProductSkeletons || undefined}
            >
              {showProductSkeletons ? <StorefrontProductSkeleton count={5} /> : null}
              {shellProducts.map((p) => (
                <StorefrontProductCard
                  key={String(p.walmart_item_id || p.id)}
                  product={p}
                  store={selectedStore}
                  columns={columns}
                  onAnalyze={onAnalyzeProduct}
                  marketCheck={
                    marketChecks[String(p.walmart_item_id || p.id || "")] || undefined
                  }
                  onCheckBuyBox={onCheckBuyBox}
                  onCheckSellers={onCheckSellers}
                  buySourcesState={
                    buySourcesByItemId[String(p.walmart_item_id || "")] || undefined
                  }
                  onRetryBuySources={onRetryBuySources}
                  onRefreshBuySources={onRefreshBuySources}
                  saved={isSaved(savedItemIds, p.walmart_item_id)}
                  saveBusy={savingIds.has(normalizeSavedItemId(p.walmart_item_id))}
                  onToggleSave={onToggleSave}
                />
              ))}
              {!products.length && !productsLoading && !showProductSkeletons ? (
                <p className="text-[12px] text-[#66758a] py-8 text-center m-0" data-testid="sfh-empty">
                  {status === "empty"
                    ? catalogScope === "featured"
                      ? "No featured products available for this seller."
                      : "This seller catalog returned no products."
                    : "No products match the current filters."}
                </p>
              ) : null}
            </section>
            {(showResults || preservingCurrentData) && (products.length || pagination) ? (
              <StorefrontPagination
                catalogScope={catalogScope}
                currentPage={page}
                pageSize={pagination?.page_size ?? 40}
                total={
                  catalogScope === "featured"
                    ? null
                    : pagination?.total ?? selectedStore?.product_count ?? null
                }
                totalPages={
                  catalogScope === "featured" ? null : pagination?.total_pages ?? null
                }
                from={pagination?.from ?? pagination?.range_from ?? null}
                to={pagination?.to ?? pagination?.range_to ?? null}
                visibleCount={products.length}
                hasNext={pagination?.has_next === true}
                hasPrevious={pagination?.has_previous === true}
                loadingDirection={pageLoadingDirection}
                onFirst={() => loadPage(1, "previous")}
                onPrevious={() => loadPage(page - 1, "previous")}
                onNext={() => loadPage(page + 1, "next")}
                onLast={(last) => loadPage(last, "next")}
              />
            ) : null}
          </>
        ) : null}
      </div>

      <SellerWatch
        open={watchOpen}
        onOpen={() => setWatchOpen(true)}
        onClose={() => setWatchOpen(false)}
        dockRef={watchDockRef}
        refreshKey={watchRefreshKey}
        selectedSellerIds={selectedSellerId ? [selectedSellerId] : []}
        onSelectSeller={(s) => {
          if (!s?.seller_id) return;
          setQuery(s.seller_id);
          void runAnalyze(s.seller_id, s);
        }}
      />

      <StorefrontFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filters}
        columns={columns}
        brandOptions={brandOptions}
        categoryOptions={categoryOptions}
        onApply={({ filters: nextFilters, columns: nextColumns }) => {
          setFilters(nextFilters);
          setColumns(nextColumns);
          persistColumnPrefs(nextColumns);
        }}
      />
    </div>
  );
}
